import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Eye,
  EyeOff,
  Globe,
  Layers,
  Pencil,
  Plug,
  Plus,
  Power,
  CreditCard,
  SlidersHorizontal,
  Store,
  Trash2,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { JsonConfigEditor } from "@/components/JsonConfigEditor";
import { ModalOverlay } from "@/components/ModalOverlay";
import { RolloutPolicyBadge } from "@/components/RolloutPolicyBadge";
export { RolloutPolicyBadge };
import { usePartnerScope } from "@/components/partner-scope-context";
import { ConfigurationSettingsTab } from "@/components/platform-catalog/ConfigurationSettingsTab";
import { PaymentMethodsCatalogTab } from "@/components/platform-catalog/PaymentMethodsCatalogTab";
import { PropagateDepartmentDialog } from "@/components/PropagateDepartmentDialog";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import type {
  Application,
  FeatureCatalogEntry,
  ProviderCatalogEntry,
  ProviderFieldSchema,
  ProviderSupportedFeature,
  RolloutPolicy,
  Vertical,
  VerticalDepartment,
} from "@tilt/admin-sdk";

/**
 * /platform/catalog - super-admin-only management of platform-scope
 * catalogs (verticals + departments, providers, applications,
 * features, configuration settings). Tab-based layout so a super-
 * admin can flip between them quickly.
 *
 * Designed for fast self-service onboarding of a new provider or a
 * new vertical: add a row, fill the form, done - no code or
 * migration required. The provider form accepts the config /
 * credential / device JSON Schemas that the rest of the admin UI
 * uses to render dynamic forms.
 */
type TabKey =
  | "verticals"
  | "providers"
  | "applications"
  | "features"
  | "settings"
  | "payment-methods";

export function PlatformCatalogPage(): JSX.Element {
  usePageTitle("Platform catalog");
  const [sp, setSp] = useSearchParams();
  const tab: TabKey = (sp.get("tab") as TabKey) || "verticals";
  const setTab = (next: TabKey): void => {
    sp.set("tab", next);
    setSp(sp);
  };

  const { scope } = usePartnerScope();
  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => apiClient.getProfile(),
    staleTime: 60_000,
  });
  const isSuper = !!profile.data?.roles.some((r) => r.role === "super_admin");

  if (!isSuper) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Platform catalogs</h1>
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          Platform-catalog management is reserved for platform admins (super_admin). If you need a
          new vertical, feature, or provider added, ask a platform admin to set it up from the
          catalog page, then you can bind it at the partner / organization / location level.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Platform catalogs</h1>
          <p className="text-sm text-muted-foreground">
            Verticals, providers, applications, and features available to every partner on the
            platform. Editable by platform admins only.
          </p>
        </div>
        <span className="text-xs text-muted-foreground">
          Acting on behalf of <span className="font-medium text-foreground">{scope.name}</span>
        </span>
      </div>

      <div className="flex flex-wrap gap-1 border-b">
        <TabButton active={tab === "verticals"} onClick={() => setTab("verticals")}>
          <Store className="h-3.5 w-3.5" aria-hidden="true" />
          Verticals
        </TabButton>
        <TabButton active={tab === "providers"} onClick={() => setTab("providers")}>
          <Globe className="h-3.5 w-3.5" aria-hidden="true" />
          Providers
        </TabButton>
        <TabButton active={tab === "applications"} onClick={() => setTab("applications")}>
          <Plug className="h-3.5 w-3.5" aria-hidden="true" />
          Apps
        </TabButton>
        <TabButton active={tab === "features"} onClick={() => setTab("features")}>
          <Layers className="h-3.5 w-3.5" aria-hidden="true" />
          Features
        </TabButton>
        <TabButton active={tab === "settings"} onClick={() => setTab("settings")}>
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
          Settings
        </TabButton>
        <TabButton active={tab === "payment-methods"} onClick={() => setTab("payment-methods")}>
          <CreditCard className="h-3.5 w-3.5" aria-hidden="true" />
          Payment Methods
        </TabButton>
      </div>

      {tab === "verticals" && <VerticalsTab />}
      {tab === "providers" && <ProvidersTab />}
      {tab === "applications" && <ApplicationsCatalogTab />}
      {tab === "features" && <FeaturesCatalogTab />}
      {tab === "settings" && <ConfigurationSettingsTab />}
      {tab === "payment-methods" && <PaymentMethodsCatalogTab />}
    </div>
  );
}

function TabButton(props: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={cn(
        "-mb-px flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-sm",
        props.active
          ? "border-primary font-medium text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {props.children}
    </button>
  );
}

// ---------------------------------------------------------------- //
// Verticals tab
// ---------------------------------------------------------------- //

function VerticalsTab(): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["verticals"], queryFn: () => apiClient.listVerticals() });
  const [createOpen, setCreateOpen] = useState(false);
  const verticals = q.data?.verticals ?? [];

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["verticals"] });
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Industry classifications for locations. Each vertical comes with a default department list
          that new locations can clone in one click.
        </p>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> New vertical
        </button>
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {verticals.length === 0 && !q.isLoading && (
        <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          No verticals yet. Add one to let partners categorise their locations.
        </div>
      )}

      <div className="space-y-3">
        {verticals.map((v) => (
          <VerticalCard key={v.id} vertical={v} onMutated={invalidate} />
        ))}
      </div>

      {createOpen && (
        <VerticalCreateDialog
          onClose={() => setCreateOpen(false)}
          onMutated={() => {
            invalidate();
            setCreateOpen(false);
          }}
        />
      )}
    </section>
  );
}

function VerticalCard(props: { vertical: Vertical; onMutated: () => void }): JSX.Element {
  const { vertical } = props;
  const [deptSlug, setDeptSlug] = useState("");
  const [deptName, setDeptName] = useState("");
  const [error, setError] = useState<string | null>(null);
  // The just-created dept stays in state until super_admin dismisses
  // the propagation dialog. Stored separately from the vertical's
  // ``departments`` array so a refetch from ``onMutated`` doesn't
  // race the dialog open.
  const [propagateFor, setPropagateFor] = useState<VerticalDepartment | null>(null);

  const toggleActive = useMutation({
    mutationFn: () => apiClient.patchVertical(vertical.id, { is_active: !vertical.is_active }),
    onSuccess: props.onMutated,
    onError: (err: unknown) => setError(describe(err)),
  });

  const delV = useMutation({
    mutationFn: () => apiClient.deleteVertical(vertical.id),
    onSuccess: props.onMutated,
    onError: (err: unknown) => setError(describe(err)),
  });

  const addDept = useMutation({
    mutationFn: () =>
      apiClient.createVerticalDepartment(vertical.id, {
        slug: deptSlug.trim(),
        display_name: deptName.trim(),
      }),
    onSuccess: (created) => {
      setDeptSlug("");
      setDeptName("");
      setError(null);
      props.onMutated();
      // Open the propagation prompt with the freshly-created dept.
      // The dialog itself decides whether to render the form (if
      // any curated scopes need this dept) or a friendly "nothing
      // to propagate" body.
      setPropagateFor(created);
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const delDept = useMutation({
    mutationFn: (deptId: string) => apiClient.deleteVerticalDepartment(vertical.id, deptId),
    onSuccess: props.onMutated,
  });

  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">{vertical.display_name}</span>
            <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
              {vertical.slug}
            </span>
            {!vertical.is_active && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                inactive
              </span>
            )}
          </div>
          {vertical.description && (
            <p className="mt-0.5 text-xs text-muted-foreground">{vertical.description}</p>
          )}
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => toggleActive.mutate()}
            className="rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
          >
            {vertical.is_active ? "Deactivate" : "Activate"}
          </button>
          <button
            type="button"
            onClick={() => {
              if (confirm(`Delete vertical "${vertical.display_name}"?`)) delV.mutate();
            }}
            className="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3 w-3" />
            Delete
          </button>
        </div>
      </div>

      <div className="mt-3 rounded-md bg-muted/30 p-3">
        <div className="mb-2 text-xs font-medium text-muted-foreground">Default departments</div>
        <ul className="space-y-1 text-xs">
          {vertical.departments.length === 0 && (
            <li className="text-muted-foreground">No default departments yet.</li>
          )}
          {vertical.departments.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-2 rounded-md bg-background px-2 py-1"
            >
              <span>
                <span className="font-medium">{d.display_name}</span>{" "}
                <span className="font-mono text-muted-foreground">{d.slug}</span>
              </span>
              <button
                type="button"
                onClick={() => delDept.mutate(d.id)}
                aria-label={`Remove ${d.display_name}`}
                className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-muted hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-2 flex flex-wrap gap-2">
          <input
            type="text"
            value={deptSlug}
            onChange={(e) => setDeptSlug(e.target.value)}
            placeholder="slug (e.g. parts)"
            className="w-32 rounded-md border bg-background px-2 py-1 text-xs font-mono"
          />
          <input
            type="text"
            value={deptName}
            onChange={(e) => setDeptName(e.target.value)}
            placeholder="Display name"
            className="flex-1 rounded-md border bg-background px-2 py-1 text-xs"
          />
          <button
            type="button"
            onClick={() => addDept.mutate()}
            disabled={!deptSlug.trim() || !deptName.trim() || addDept.isPending}
            className="rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            Add
          </button>
        </div>
      </div>
      {error && (
        <div className="mt-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
          {error}
        </div>
      )}
      {propagateFor && (
        <PropagateDepartmentDialog
          verticalId={vertical.id}
          verticalName={vertical.display_name}
          department={propagateFor}
          onClose={() => setPropagateFor(null)}
        />
      )}
    </div>
  );
}

function VerticalCreateDialog(props: { onClose: () => void; onMutated: () => void }): JSX.Element {
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      apiClient.createVertical({
        slug: slug.trim(),
        display_name: displayName.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
      }),
    onSuccess: props.onMutated,
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <ModalOverlay ariaLabel="New vertical" onClose={props.onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="w-full max-w-md space-y-3 rounded-t-xl border bg-card p-5 shadow-lg sm:rounded-xl"
      >
        <h2 className="text-sm font-semibold">New vertical</h2>
        <Field label="Slug" id="v-slug" hint="Lowercase, underscore-separated.">
          <input
            id="v-slug"
            required
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="e.g. auto_dealer"
            className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
          />
        </Field>
        <Field label="Display name" id="v-name">
          <input
            id="v-name"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Auto dealer"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Description (optional)" id="v-desc">
          <textarea
            id="v-desc"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
        </Field>
        {error && (
          <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
        )}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={!slug.trim() || !displayName.trim() || create.isPending}
            className="flex-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            Create
          </button>
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

function Field(props: {
  label: string;
  id: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={props.id} className="mb-1 block text-xs font-medium text-muted-foreground">
        {props.label}
      </label>
      {props.children}
      {props.hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{props.hint}</p>}
    </div>
  );
}

// ---------------------------------------------------------------- //
// Providers tab
// ---------------------------------------------------------------- //

function ProvidersTab(): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["provider-catalog"],
    queryFn: () => apiClient.listProviderCatalog(),
  });
  const providers = q.data?.providers ?? [];
  const [createOpen, setCreateOpen] = useState(false);
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["provider-catalog"] });
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Payment / ACH / crypto provider integrations. Each entry defines the config + credential
          fields the admin UI renders dynamically - no code needed to add a new provider.
        </p>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> New provider
        </button>
      </div>
      {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {providers.map((p) => (
          <ProviderCard key={p.id} provider={p} onMutated={invalidate} />
        ))}
      </div>
      {createOpen && (
        <ProviderCreateDialog
          onClose={() => setCreateOpen(false)}
          onMutated={() => {
            invalidate();
            setCreateOpen(false);
          }}
        />
      )}
    </section>
  );
}

function ProviderCard(props: {
  provider: ProviderCatalogEntry;
  onMutated: () => void;
}): JSX.Element {
  const { provider } = props;
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const toggle = useMutation({
    mutationFn: () =>
      apiClient.patchProviderCatalogEntry(provider.id, {
        is_enabled: !provider.is_enabled,
      }),
    onSuccess: props.onMutated,
  });
  const delP = useMutation({
    mutationFn: () => apiClient.deleteProviderCatalogEntry(provider.id),
    onSuccess: props.onMutated,
  });

  return (
    <div className="flex flex-col rounded-lg border bg-card p-4 shadow-sm">
      {/* Header - name + slug + status pills */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-medium">{provider.display_name}</span>
        <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
          {provider.slug}
        </span>
        {!provider.is_enabled && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
            disabled
          </span>
        )}
        {provider.supports_devices && (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
            devices
          </span>
        )}
      </div>

      {/* Body - description, features, vendor docs */}
      <div className="mt-1.5 flex-1 space-y-1">
        {provider.description && (
          <p className="line-clamp-2 text-xs text-muted-foreground">{provider.description}</p>
        )}
        <p className="text-[11px] text-muted-foreground">
          features:{" "}
          {provider.supported_feature_paths.length === 0
            ? "-"
            : provider.supported_feature_paths.join(", ")}
        </p>
        {provider.vendor_docs_url && (
          <a
            href={provider.vendor_docs_url}
            target="_blank"
            rel="noreferrer"
            className="block text-[11px] text-primary hover:underline"
          >
            vendor docs →
          </a>
        )}
      </div>

      {/* Footer - compact icon-button row pinned to the bottom of the tile */}
      <div className="mt-3 flex items-center gap-1 border-t pt-3">
        <button
          type="button"
          onClick={() => setEditing(true)}
          title="Edit"
          aria-label="Edit"
          className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
        >
          <Pencil className="h-3 w-3" />
          Edit
        </button>
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          title={expanded ? "Hide schemas" : "View schemas"}
          aria-label={expanded ? "Hide schemas" : "View schemas"}
          className="inline-flex items-center rounded-md border bg-background p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          {expanded ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => toggle.mutate()}
          disabled={toggle.isPending}
          title={provider.is_enabled ? "Disable globally" : "Enable globally"}
          aria-label={provider.is_enabled ? "Disable globally" : "Enable globally"}
          className={cn(
            "inline-flex items-center rounded-md border bg-background p-1.5 hover:bg-muted disabled:opacity-50",
            provider.is_enabled
              ? "text-emerald-700 dark:text-emerald-300"
              : "text-muted-foreground",
          )}
        >
          <Power className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => {
            if (confirm(`Delete provider "${provider.display_name}"?`)) delP.mutate();
          }}
          title="Delete"
          aria-label="Delete"
          className="ml-auto inline-flex items-center rounded-md border border-destructive/40 bg-destructive/5 p-1.5 text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="mt-3 space-y-3 border-t pt-3">
          <SchemaBlock title="Config fields (non-secret)" schema={provider.config_schema} />
          <SchemaBlock title="Credential fields (encrypted)" schema={provider.credential_schema} />
          {provider.supports_devices && (
            <SchemaBlock title="Device fields" schema={provider.device_schema} />
          )}
        </div>
      )}

      {editing && (
        <ProviderEditDialog
          provider={provider}
          onClose={() => setEditing(false)}
          onMutated={() => {
            setEditing(false);
            props.onMutated();
          }}
        />
      )}
    </div>
  );
}

function SchemaBlock(props: { title: string; schema: ProviderFieldSchema }): JSX.Element {
  const entries = Object.entries(props.schema.properties ?? {});
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-muted-foreground">{props.title}</div>
      {entries.length === 0 ? (
        <p className="text-xs text-muted-foreground">No fields defined.</p>
      ) : (
        <ul className="grid gap-1 text-xs sm:grid-cols-2">
          {entries.map(([name, spec]) => (
            <li key={name} className="rounded-md bg-muted/30 px-2 py-1 font-mono">
              <span className="font-medium">{name}</span>
              <span className="text-muted-foreground"> · {spec.type}</span>
              {spec.required && <span className="text-primary"> · required</span>}
              {spec.secret && <span className="text-destructive"> · secret</span>}
              {spec.hint && (
                <p className="mt-0.5 font-sans text-[11px] text-muted-foreground">{spec.hint}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProviderCreateDialog(props: { onClose: () => void; onMutated: () => void }): JSX.Element {
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [vendorDocsUrl, setVendorDocsUrl] = useState("");
  const [supportsDevices, setSupportsDevices] = useState(false);
  const [configSchema, setConfigSchema] = useState<Record<string, unknown>>({});
  const [credSchema, setCredSchema] = useState<Record<string, unknown>>({});
  const [deviceSchema, setDeviceSchema] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      apiClient.createProviderCatalogEntry({
        slug: slug.trim(),
        display_name: displayName.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(vendorDocsUrl.trim() ? { vendor_docs_url: vendorDocsUrl.trim() } : {}),
        supports_devices: supportsDevices,
        config_schema: configSchema as ProviderFieldSchema,
        credential_schema: credSchema as ProviderFieldSchema,
        device_schema: deviceSchema as ProviderFieldSchema,
      }),
    onSuccess: props.onMutated,
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <ModalOverlay ariaLabel="New provider" onClose={props.onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="w-full max-w-2xl space-y-4 rounded-t-xl border bg-card p-5 shadow-lg sm:rounded-xl"
      >
        <h2 className="text-sm font-semibold">New provider</h2>
        <p className="text-xs text-muted-foreground">
          Paste the field schemas the admin UI should render when someone binds this provider to a
          scope. Keep secrets in the credential schema (they go through the KMS envelope); config
          fields are JSONB rendered as a form.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Slug" id="p-slug" hint="e.g. stripe_connect">
            <input
              id="p-slug"
              required
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
            />
          </Field>
          <Field label="Display name" id="p-name">
            <input
              id="p-name"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </Field>
        </div>
        <Field label="Description" id="p-desc">
          <input
            id="p-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Vendor docs URL" id="p-url">
          <input
            id="p-url"
            type="url"
            value={vendorDocsUrl}
            onChange={(e) => setVendorDocsUrl(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
        </Field>
        <p className="rounded-md border border-dashed bg-muted/30 p-2 text-[11px] text-muted-foreground">
          Once the provider exists, declare which feature_paths it serves via{" "}
          <code className="font-mono">provider_feature_support_tbl</code> (super_admin CRUD coming
          to this page; for now, edit via migration or admin-api).
        </p>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={supportsDevices}
            onChange={(e) => setSupportsDevices(e.target.checked)}
            className="h-3.5 w-3.5 accent-primary"
          />
          Supports per-location devices (terminals, card readers, …)
        </label>

        <div className="rounded-md border bg-muted/30 p-3">
          <JsonConfigEditor
            label="Config schema"
            hint="JSON Schema-lite: keys = property names, values = {type, title, required, hint}."
            value={configSchema}
            onChange={setConfigSchema}
          />
        </div>
        <div className="rounded-md border bg-muted/30 p-3">
          <JsonConfigEditor
            label="Credential schema"
            hint="Same shape; mark fields with `secret: true` so they go through KMS envelope."
            value={credSchema}
            onChange={setCredSchema}
          />
        </div>
        {supportsDevices && (
          <div className="rounded-md border bg-muted/30 p-3">
            <JsonConfigEditor
              label="Device schema"
              hint="Per-device fields - device_ref, display_name, vendor-specific metadata."
              value={deviceSchema}
              onChange={setDeviceSchema}
            />
          </div>
        )}
        {error && (
          <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
        )}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={!slug.trim() || !displayName.trim() || create.isPending}
            className="flex-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            Create provider
          </button>
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

/**
 * Provider-aware help text for the Integration-config editor. The
 * shape ({"<env>": {...}}) is universal but the per-key conventions
 * vary - Valor uses ISO-keyed URLs, Aeropay uses scope+user_id+
 * merchant_uuid, Bead uses crypto-acceptance settings. Picking the
 * right example for the row being edited stops the form from
 * confusing admins (2026-04-27 dogfood - the hardcoded Valor
 * example was showing on the Aeropay row).
 */
function integrationConfigHint(providerSlug: string, displayName: string): string {
  const generic =
    "Vendor-constant defaults keyed by environment. Shape: " +
    '{"sandbox": {…}, "production": {…}}. Per-binding config (admin overrides) ' +
    "lives on the binding itself; per-merchant secrets live on the credential row.";
  if (providerSlug === "valor") {
    return (
      generic +
      ' Example: {"sandbox": {"api_base_url_merchant": "https://demo.valorpaytech.com/api/Valor"}, ' +
      '"production": {"api_base_url_merchant": "https://online.valorpaytech.com/api/Valor"}}.'
    );
  }
  if (providerSlug === "aeropay") {
    return (
      generic +
      ' Example: {"sandbox": {"api_base_url": "https://api.sandbox-pay.aero.inc", ' +
      '"scope": "merchant", "user_id": "3111", "merchant_uuid": "4adfe560d6"}, ' +
      '"production": {"api_base_url": "https://api.aeropay.com", "scope": "merchant"}}.'
    );
  }
  if (providerSlug === "bead") {
    return (
      generic +
      ' Example: {"sandbox": {"api_base_url": "https://api-sandbox.bead.xyz"}, ' +
      '"production": {"api_base_url": "https://api.beadpay.io"}}.'
    );
  }
  return `${generic} See ${displayName}'s vendor docs for the field set this provider expects.`;
}

/**
 * Manage which feature_paths a provider supports. Backed by
 * provider_feature_support_tbl (V0060) - adding a row says "valor can
 * serve pos.payments.virtual"; removing it un-says that. Per-scope
 * DISALLOW rows (feature_provider_allowlist_tbl) constrain further
 * down the cascade and live on a separate UI surface.
 *
 * Shows the current set as removable chips, plus a picker that's
 * filtered to bindable feature leaves the provider doesn't already
 * cover. The DELETE endpoint surfaces a 409 when the pair is still
 * referenced by any provider_binding_tbl row - the toast bubbles up
 * unchanged so admin sees the actual blocker.
 */
function ProviderFeaturesPanel(props: { provider: ProviderCatalogEntry }): JSX.Element {
  const { provider } = props;
  const qc = useQueryClient();
  const featuresQ = useQuery({
    queryKey: ["provider-features", provider.id],
    queryFn: () => apiClient.listProviderFeatures(provider.id),
    initialData: {
      feature_paths: provider.supported_feature_paths,
      features: provider.supported_features ?? [],
    },
  });
  const allFeatures = useQuery({
    queryKey: ["features-catalog"],
    queryFn: () => apiClient.listFeatureCatalog(),
    staleTime: 60_000,
  });
  const [picker, setPicker] = useState("");
  const [error, setError] = useState<string | null>(null);

  // V0063 - features array carries per-(provider, feature) rollout_policy.
  // Fall back to feature_paths for the legacy shape.
  const current: ProviderSupportedFeature[] =
    featuresQ.data?.features ??
    (featuresQ.data?.feature_paths ?? []).map((fp) => ({
      feature_path: fp,
      rollout_policy: "broadcast" as const,
    }));
  const currentPaths = current.map((f) => f.feature_path);
  const bindableLeaves = (allFeatures.data?.features ?? [])
    .filter((f) => f.is_provider_bindable)
    .map((f) => f.path)
    .filter((fp) => !currentPaths.includes(fp))
    .sort();

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["provider-features", provider.id] });
    // Refresh the provider catalog list so other UI (BindingDialog
    // pickers) sees the new supported_feature_paths array.
    void qc.invalidateQueries({ queryKey: ["provider-catalog"] });
  };

  const add = useMutation({
    mutationFn: (feature_path: string) =>
      apiClient.addProviderFeature(provider.id, { feature_path }),
    onSuccess: () => {
      setError(null);
      setPicker("");
      invalidate();
    },
    onError: (e: unknown) => setError(describe(e)),
  });

  const remove = useMutation({
    mutationFn: (feature_path: string) =>
      apiClient.removeProviderFeature(provider.id, feature_path),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e: unknown) => setError(describe(e)),
  });

  // V0063 - flip rollout_policy on a (provider, feature) pair. Used by
  // the chip's badge button.
  const patchRollout = useMutation({
    mutationFn: ({
      feature_path,
      rollout_policy,
    }: {
      feature_path: string;
      rollout_policy: RolloutPolicy;
    }) =>
      apiClient.patchProviderFeatureRollout(provider.id, feature_path, {
        rollout_policy,
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e: unknown) => setError(describe(e)),
  });

  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="mb-2">
        <p className="text-xs font-medium">Supported features</p>
        <p className="text-[11px] text-muted-foreground">
          Which feature_paths this provider can serve at the catalog level, plus the rollout policy
          per pair. <strong>Live</strong> = on for everyone unless an admin opts a scope out.{" "}
          <strong>Pilot</strong> = off for everyone unless an admin explicitly grants a scope. Click
          the badge to flip.
        </p>
      </div>
      <ul className="mb-2 flex flex-wrap gap-1.5">
        {current.length === 0 && (
          <li className="text-[11px] italic text-muted-foreground">
            No features configured yet - add one below.
          </li>
        )}
        {current.map((feat) => {
          const isLive = feat.rollout_policy === "broadcast";
          const nextPolicy: RolloutPolicy = isLive ? "controlled" : "broadcast";
          return (
            <li
              key={feat.feature_path}
              className="flex items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-[11px]"
            >
              <span className="font-mono">{feat.feature_path}</span>
              <button
                type="button"
                aria-label={`Flip ${feat.feature_path} to ${nextPolicy}`}
                disabled={patchRollout.isPending}
                onClick={() =>
                  patchRollout.mutate({
                    feature_path: feat.feature_path,
                    rollout_policy: nextPolicy,
                  })
                }
                title={`Click to set ${nextPolicy}`}
                className={cn(
                  "rounded-sm px-1.5 py-0.5 text-[10px] font-medium uppercase",
                  isLive
                    ? "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/25 dark:text-emerald-300"
                    : "bg-amber-500/15 text-amber-700 hover:bg-amber-500/25 dark:text-amber-300",
                  "disabled:opacity-40",
                )}
              >
                {isLive ? "Live" : "Pilot"}
              </button>
              <button
                type="button"
                aria-label={`Remove ${feat.feature_path}`}
                disabled={remove.isPending}
                onClick={() => remove.mutate(feat.feature_path)}
                className="text-muted-foreground hover:text-destructive disabled:opacity-40"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center gap-2">
        <select
          value={picker}
          onChange={(e) => setPicker(e.target.value)}
          className="flex-1 rounded-md border bg-background px-2 py-1.5 text-xs"
        >
          <option value="">Pick a feature to add…</option>
          {bindableLeaves.map((fp) => (
            <option key={fp} value={fp}>
              {fp}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!picker || add.isPending}
          onClick={() => add.mutate(picker)}
          className="rounded-md border bg-background px-2 py-1.5 text-xs hover:bg-muted disabled:opacity-40"
        >
          {add.isPending ? "Adding…" : "Add"}
        </button>
      </div>
      <p className="mt-2 text-[10px] text-muted-foreground">
        New features default to <strong>Pilot</strong> - they're off everywhere until you grant a
        partner explicitly via the Allowed providers tab on a partner / org / location detail page.
        Flip to <strong>Live</strong> when ready to ship to everyone.
      </p>
      {error && <p className="mt-2 text-[11px] text-destructive">{error}</p>}
    </div>
  );
}

/**
 * Edit an existing provider catalog entry. Mirrors ProviderCreateDialog
 * but slug is immutable (it's the identity used by bindings + catalog
 * seeds across the platform; renaming it would orphan everything). PATCH
 * only sends fields the admin actually edits - the handler already
 * accepts Partial<create> per ProviderCatalogPatchRequest.
 */
function ProviderEditDialog(props: {
  provider: ProviderCatalogEntry;
  onClose: () => void;
  onMutated: () => void;
}): JSX.Element {
  const { provider } = props;
  const [displayName, setDisplayName] = useState(provider.display_name);
  const [description, setDescription] = useState(provider.description ?? "");
  const [vendorDocsUrl, setVendorDocsUrl] = useState(provider.vendor_docs_url ?? "");
  const [supportsDevices, setSupportsDevices] = useState(provider.supports_devices);
  const [configSchema, setConfigSchema] = useState<Record<string, unknown>>(
    provider.config_schema as unknown as Record<string, unknown>,
  );
  const [credSchema, setCredSchema] = useState<Record<string, unknown>>(
    provider.credential_schema as unknown as Record<string, unknown>,
  );
  const [deviceSchema, setDeviceSchema] = useState<Record<string, unknown>>(
    provider.device_schema as unknown as Record<string, unknown>,
  );
  // Tier-1 integration config - vendor-constant defaults per env.
  // Shape: {"sandbox": {...}, "production": {...}}. Free-form below
  // the top-level env keys so a new provider can declare whatever
  // defaults it needs without a schema change.
  const [integrationConfig, setIntegrationConfig] = useState<Record<string, unknown>>(
    (provider.integration_config as unknown as Record<string, unknown>) ?? {},
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      apiClient.patchProviderCatalogEntry(provider.id, {
        display_name: displayName.trim(),
        // description / vendor_docs_url omitted from payload when
        // blank - the PATCH type inherits `string | undefined` from
        // Partial<create>, not `string | null`. Treat blank as
        // "no change" for now; explicit clear-to-null would need a
        // backend patch type extension.
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(vendorDocsUrl.trim() ? { vendor_docs_url: vendorDocsUrl.trim() } : {}),
        supports_devices: supportsDevices,
        config_schema: configSchema as ProviderFieldSchema,
        credential_schema: credSchema as ProviderFieldSchema,
        device_schema: deviceSchema as ProviderFieldSchema,
        integration_config: integrationConfig as Record<string, Record<string, unknown>>,
      }),
    onSuccess: props.onMutated,
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <ModalOverlay ariaLabel={`Edit provider ${provider.slug}`} onClose={props.onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="w-full max-w-2xl space-y-4 rounded-t-xl border bg-card p-5 shadow-lg sm:rounded-xl"
      >
        <div>
          <h2 className="text-sm font-semibold">Edit {provider.display_name}</h2>
          <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
            slug: {provider.slug} (immutable)
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Display name" id="pe-name">
            <input
              id="pe-name"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Vendor docs URL" id="pe-url">
            <input
              id="pe-url"
              type="url"
              value={vendorDocsUrl}
              onChange={(e) => setVendorDocsUrl(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </Field>
        </div>
        <Field label="Description" id="pe-desc">
          <input
            id="pe-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
        </Field>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={supportsDevices}
            onChange={(e) => setSupportsDevices(e.target.checked)}
            className="h-3.5 w-3.5 accent-primary"
          />
          Supports per-location devices (terminals, card readers, …)
        </label>

        <ProviderFeaturesPanel provider={provider} />

        <div className="rounded-md border bg-muted/30 p-3">
          <JsonConfigEditor
            label="Config schema"
            hint="JSON Schema-lite: keys = property names, values = {type, title, required, hint, source?}."
            value={configSchema}
            onChange={setConfigSchema}
          />
        </div>
        <div className="rounded-md border bg-muted/30 p-3">
          <JsonConfigEditor
            label="Credential schema"
            hint="Same shape; mark fields with `secret: true` so they go through KMS envelope."
            value={credSchema}
            onChange={setCredSchema}
          />
        </div>
        {supportsDevices && (
          <div className="rounded-md border bg-muted/30 p-3">
            <JsonConfigEditor
              label="Device schema"
              hint="Per-device fields - device_ref, display_name, vendor-specific metadata."
              value={deviceSchema}
              onChange={setDeviceSchema}
            />
          </div>
        )}
        <div className="rounded-md border bg-muted/30 p-3">
          <JsonConfigEditor
            label="Integration config (Tier-1 defaults, per env)"
            hint={integrationConfigHint(provider.slug, provider.display_name)}
            value={integrationConfig}
            onChange={setIntegrationConfig}
          />
        </div>
        {error && (
          <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
        )}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={!displayName.trim() || save.isPending}
            className="flex-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </button>
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// --------------------------------------------------------------------- //
// V0046 + V0060 catalog tabs                                            //
// --------------------------------------------------------------------- //

/** Apps tab - read-only listing of the platform application catalogue
 *  (V0046). Adds + edits land via migration-bundled seeds today, since
 *  the platform ships a fixed set ("pos", "admin"); a future PR will
 *  add inline create / retire if the list grows. Showing this here
 *  gives super_admins a single page to confirm what apps exist + which
 *  are active. */
function ApplicationsCatalogTab(): JSX.Element {
  const q = useQuery({
    queryKey: ["platform-applications"],
    queryFn: () => apiClient.listPlatformApplications(),
  });
  const apps: readonly Application[] = q.data?.applications ?? [];
  return (
    <section className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Top-level apps every partner can opt into. The Applications tab on Partner / Org / Location
        toggles which of these are enabled at each scope (cascading down). Apps are the coarse gate;
        features (ltree paths under each app) are the fine gate.
      </p>

      {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!q.isLoading && apps.length === 0 && (
        <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          No applications catalogued. Migrations should seed at least the platform-level "pos" and
          "admin" rows - if this is empty, V0046 hasn't applied.
        </div>
      )}

      <ul className="divide-y rounded-md border bg-card">
        {[...apps]
          .sort((a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug))
          .map((app) => (
            <li
              key={app.slug}
              className="flex flex-wrap items-start justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {app.is_active ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground" aria-hidden />
                  )}
                  <span className="text-sm font-medium">{app.display_name}</span>
                  <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase text-muted-foreground">
                    {app.slug}
                  </span>
                  {!app.is_active && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                      Retired
                    </span>
                  )}
                </div>
                {app.description && (
                  <p className="mt-1 text-xs text-muted-foreground">{app.description}</p>
                )}
              </div>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                sort {app.sort_order}
              </span>
            </li>
          ))}
      </ul>
    </section>
  );
}

// RolloutPolicyBadge lives in @/components/RolloutPolicyBadge and is re-exported above.

/** Features tab - V0060 feature_tbl ltree catalogue. Rows are clickable links
 *  to the FeatureDetailPage for rollout management. V0089 adds the Rollout column. */
function FeaturesCatalogTab(): JSX.Element {
  const q = useQuery({
    queryKey: ["feature-catalog"],
    queryFn: () => apiClient.listFeatureCatalog(),
  });
  const features: readonly FeatureCatalogEntry[] = q.data?.features ?? [];
  const sorted = [...features].sort(
    (a, b) => a.path.localeCompare(b.path) || a.sort_order - b.sort_order || a.depth - b.depth,
  );

  return (
    <section className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Feature ltree paths under each app. Each path is a feature you can toggle on/off at any
        scope from the Applications tab. Provider-bindable leaves are where you bind a Valor /
        Aeropay / DMS provider to deliver that feature. Click any row to manage its rollout policy
        and provider support.
      </p>

      {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!q.isLoading && features.length === 0 && (
        <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          No features catalogued. The V0060 migration seeds the canonical set; if this is empty the
          migration hasn't applied yet.
        </div>
      )}

      <ul className="divide-y rounded-md border bg-card">
        {sorted.map((f) => {
          const indent = Math.max(0, f.depth - 1) * 16;
          return (
            <li key={f.path} style={{ paddingLeft: indent + 16 }}>
              <Link
                to={`/platform/catalog/features/${encodeURIComponent(f.path)}`}
                className="flex flex-wrap items-start justify-between gap-3 py-2.5 pr-4 hover:bg-muted/40"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {f.is_active ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
                    ) : (
                      <XCircle className="h-4 w-4 text-muted-foreground" aria-hidden />
                    )}
                    <span className="text-sm font-medium">{f.display_name}</span>
                    {f.rollout_policy && <RolloutPolicyBadge policy={f.rollout_policy} />}
                    {f.is_provider_bindable && (
                      <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-800 dark:bg-sky-900/30 dark:text-sky-200">
                        Provider-bindable
                      </span>
                    )}
                    {!f.is_active && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                        Retired
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">{f.path}</div>
                  {f.description && (
                    <p className="mt-1 text-xs text-muted-foreground">{f.description}</p>
                  )}
                </div>
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  depth {f.depth} · sort {f.sort_order}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
